/**
 * DialogBox - RPG-style bottom bar dialog with portrait and typewriter effect
 */

import React, { useEffect, useState, useRef } from "react";

interface DialogBoxProps {
  visible: boolean;
  speaker: string;
  portrait?: string;
  text: string;
  /** Called when user clicks to skip/advance */
  onAdvance?: () => void;
}

export const DialogBox: React.FC<DialogBoxProps> = ({
  visible,
  speaker,
  portrait,
  text,
  onAdvance,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const prevVisibleRef = useRef(visible);

  // Handle visibility transitions
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      // Appearing
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 300);
    } else if (!visible && prevVisibleRef.current) {
      // Disappearing
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 200);
    }
    prevVisibleRef.current = visible;
  }, [visible]);

  if (!visible && !isAnimating) {
    return null;
  }

  return (
    <div
      className={`
        absolute bottom-0 left-0 right-0 z-[150] p-4
        transition-all duration-300 ease-out
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
      `}
      onClick={onAdvance}
    >
      {/* Dialog Container */}
      <div className="max-w-4xl mx-auto">
        <div className="relative bg-gradient-to-b from-[#1a1f2e] to-[#0f1219] border-2 border-[#3b4a6b] rounded-xl overflow-hidden shadow-2xl">
          {/* Decorative top border */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

          <div className="flex items-stretch">
            {/* Portrait Section */}
            {portrait && (
              <div className="w-32 h-32 flex-shrink-0 bg-[#0a0d14] border-r border-[#3b4a6b] flex items-center justify-center p-2">
                <img
                  src={portrait}
                  alt={speaker}
                  className="max-w-full max-h-full object-contain"
                  style={{ imageRendering: "pixelated" }}
                />
              </div>
            )}

            {/* Text Section */}
            <div className="flex-1 p-4 min-h-[120px]">
              {/* Speaker Name */}
              <div className="mb-2">
                <span
                  className="inline-block px-3 py-1 text-sm font-bold text-white rounded"
                  style={{
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                    textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                  }}
                >
                  {speaker}
                </span>
              </div>

              {/* Dialog Text */}
              <div className="text-white text-lg leading-relaxed font-medium">
                <DialogText text={text} />
              </div>
            </div>

            {/* Advance Indicator */}
            <div className="absolute bottom-3 right-4">
              <div className="animate-bounce">
                <span className="text-blue-400 text-xl">▼</span>
              </div>
            </div>
          </div>

          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-500 rounded-tl" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-500 rounded-tr" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-500 rounded-bl" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-500 rounded-br" />
        </div>
      </div>
    </div>
  );
};

/**
 * DialogText - Renders text with optional formatting
 * Supports basic tags like [shake], [wave], [color=red]
 */
function DialogText({ text }: { text: string }) {
  // For now, just render plain text
  // Can be extended to support rich text formatting
  return (
    <span className="whitespace-pre-wrap">
      {text}
      {/* Blinking cursor at the end */}
      <span className="inline-block w-[2px] h-[1em] bg-white ml-1 animate-pulse" />
    </span>
  );
}

// ============================================================================
// Alternative Dialog Styles (can be extended)
// ============================================================================

interface DialogBoxMinimalProps {
  visible: boolean;
  speaker: string;
  text: string;
}

/**
 * Minimal dialog box style - just text with speaker name
 */
export const DialogBoxMinimal: React.FC<DialogBoxMinimalProps> = ({
  visible,
  speaker,
  text,
}) => {
  if (!visible) return null;

  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[150] max-w-xl w-full px-4">
      <div className="bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-white/20">
        <div className="text-sm text-blue-400 font-bold mb-1">{speaker}</div>
        <div className="text-white text-base">{text}</div>
      </div>
    </div>
  );
};

/**
 * Speech bubble style dialog
 */
export const DialogBubble: React.FC<
  DialogBoxMinimalProps & { position?: { x: number; y: number } }
> = ({ visible, speaker, text, position }) => {
  if (!visible) return null;

  return (
    <div
      className="absolute z-[150] pointer-events-none"
      style={{
        left: position?.x ?? "50%",
        top: position?.y ?? "50%",
        transform: "translate(-50%, -100%)",
      }}
    >
      <div className="relative bg-white rounded-xl p-3 shadow-lg max-w-xs">
        <div className="text-xs text-gray-600 font-bold mb-1">{speaker}</div>
        <div className="text-gray-900 text-sm">{text}</div>
        {/* Speech bubble tail */}
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45" />
      </div>
    </div>
  );
};





























